import { useEffect, useCallback } from "react";
import { useWebSocketAtendimentoContext } from "../../contexts/atendimento/WebSocketAtendimentoContext";
import { IWebSocketNovaMensagemRecebida } from "../../interface/atendimento/websocket/IWebSocketNovaMensagemRecebida";
import { IWebSocketNovaMensagemEnviada } from "../../interface/atendimento/websocket/IWebSocketNovaMensagemEnviada";
import { IWebSocketNovaMensagem } from "../../interface/atendimento/websocket/IWebSocketNovaMensagem";
import { IWebSocketMensagemEnviada } from "../../interface/atendimento/websocket/IWebSocketMensagemEnviada";
import { IWebSocketEnviarMensagem } from "../../interface/atendimento/websocket/IWebSocketEnviarMensagem";
import { IWebSocketErro } from "../../interface/atendimento/websocket/IWebSocketErro";
import { IAlertaSessaoMensagens } from "../../interface/atendimento/IAlertaSessaoMensagens";
import { IWebSocketStatusMensagem } from "../../interface/atendimento/websocket/IWebSocketStatusMensagem";
import { IWebSocketMensagemEditada } from "../../interface/atendimento/websocket/IWebSocketMensagemEditada";

export const useWebSocketAtendimento = () => {
    const { socket, isConnected, isConnecting, reconnectionAttempt, error, reconnect } = useWebSocketAtendimentoContext();

    // ==================== LISTENERS ====================

    /**
     * Listener para evento: nova-mensagem-recebida
     * Disparado quando um cliente envia uma mensagem via WhatsApp
     */
    const onNovaMensagemRecebida = useCallback((callback: (data: IWebSocketNovaMensagemRecebida) => void) => {
        if (!socket) return;

        socket.on('nova-mensagem-recebida', callback);

        return () => {
            socket.off('nova-mensagem-recebida', callback);
        };
    }, [socket]);

    /**
     * Listener para evento: nova-mensagem-enviada
     * Disparado quando uma mensagem é enviada (atendente, IA, sistema ou atualização de pedido)
     */
    const onNovaMensagemEnviada = useCallback((callback: (data: IWebSocketNovaMensagemEnviada) => void) => {
        if (!socket) return;

        socket.on('nova-mensagem-enviada', callback);

        return () => {
            socket.off('nova-mensagem-enviada', callback);
        };
    }, [socket]);

    /**
     * Listener para evento: nova-mensagem
     * Disparado para atualizações em tempo real dentro de um chat específico
     */
    const onNovaMensagem = useCallback((callback: (data: IWebSocketNovaMensagem) => void) => {
        if (!socket) return;

        socket.on('nova-mensagem', callback);

        return () => {
            socket.off('nova-mensagem', callback);
        };
    }, [socket]);

    /**
     * Listener para evento: mensagem-enviada
     * Confirmação de que sua mensagem foi enviada com sucesso via WebSocket
     */
    const onMensagemEnviada = useCallback((callback: (data: IWebSocketMensagemEnviada) => void) => {
        if (!socket) return;

        socket.on('mensagem-enviada', callback);

        return () => {
            socket.off('mensagem-enviada', callback);
        };
    }, [socket]);

    /**
     * Listener para evento: entrou-chat
     * Confirmação de que você entrou com sucesso na sala de um chat específico
     */
    const onEntrouChat = useCallback((callback: (data: { chatUuid: string }) => void) => {
        if (!socket) return;

        socket.on('entrou-chat', callback);

        return () => {
            socket.off('entrou-chat', callback);
        };
    }, [socket]);

    /**
     * Listener para evento: saiu-chat
     * Confirmação de que você saiu da sala de um chat específico
     */
    const onSaiuChat = useCallback((callback: (data: { chatUuid: string }) => void) => {
        if (!socket) return;

        socket.on('saiu-chat', callback);

        return () => {
            socket.off('saiu-chat', callback);
        };
    }, [socket]);

    /**
     * Listener para evento: erro
     * Erros gerais do WebSocket
     */
    const onErro = useCallback((callback: (data: IWebSocketErro) => void) => {
        if (!socket) return;

        socket.on('erro', callback);

        return () => {
            socket.off('erro', callback);
        };
    }, [socket]);

    /**
     * Listener para evento: chat-lido
     * Disparado quando um chat é marcado como lido
     */
    const onChatLido = useCallback((callback: (data: { chatUuid: string }) => void) => {
        if (!socket) return;

        socket.on('chat-lido', callback);

        return () => {
            socket.off('chat-lido', callback);
        };
    }, [socket]);

    /**
     * Listener para evento: alerta-chat
     * Disparado quando um alerta é criado em um chat
     */
    const onAlertaChat = useCallback((callback: (data: { chatUuid: string; alerta: IAlertaSessaoMensagens }) => void) => {
        if (!socket) return;

        socket.on('alerta-chat', callback);

        return () => {
            socket.off('alerta-chat', callback);
        };
    }, [socket]);

    /**
     * Listener para evento: alerta-chat-removido
     * Disparado quando um alerta é removido de um chat
     */
    const onAlertaChatRemovido = useCallback((callback: (data: { chatUuid: string }) => void) => {
        if (!socket) return;

        socket.on('alerta-chat-removido', callback);

        return () => {
            socket.off('alerta-chat-removido', callback);
        };
    }, [socket]);

    /**
     * Listener para evento: status-pedido-chat
     * Disparado quando o status do pedido ativo de um chat é atualizado
     */
    const onStatusPedidoChat = useCallback((callback: (data: { chatUuid: string; pedidoUuid: string | null; status: string | null; pagamentos?: { metodoPagamento?: string; valor?: number; valorTroco?: number; valorRecebido?: number; pago?: boolean }[] }) => void) => {
        if (!socket) return;

        socket.on('status-pedido-chat', callback);

        return () => {
            socket.off('status-pedido-chat', callback);
        };
    }, [socket]);

    /**
     * Listener para evento: status-mensagem
     * Disparado quando o status de entrega/leitura de uma mensagem enviada é atualizado pelo WhatsApp
     */
    const onStatusMensagem = useCallback((callback: (data: IWebSocketStatusMensagem) => void) => {
        if (!socket) return;

        socket.on('status-mensagem', callback);

        return () => {
            socket.off('status-mensagem', callback);
        };
    }, [socket]);

    /**
     * Listener para evento: mensagem-editada
     * Disparado quando um cliente edita uma mensagem enviada via WhatsApp
     */
    const onMensagemEditada = useCallback((callback: (data: IWebSocketMensagemEditada) => void) => {
        if (!socket) return;

        socket.on('mensagem-editada', callback);

        return () => {
            socket.off('mensagem-editada', callback);
        };
    }, [socket]);

    // ==================== EMIT EVENTS ====================

    /**
     * Emitir evento: enviar-mensagem-whatsapp
     * Para enviar uma mensagem via WhatsApp
     */
    const enviarMensagemWhatsApp = useCallback((data: IWebSocketEnviarMensagem) => {
        if (!socket || !isConnected) {
            console.error('Socket não conectado');
            return false;
        }

        socket.emit('enviar-mensagem-whatsapp', data);
        return true;
    }, [socket, isConnected]);

    /**
     * Emitir evento: entrar-chat
     * Para entrar na sala de um chat específico e receber atualizações em tempo real
     */
    const entrarChat = useCallback((chatUuid: string) => {
        if (!socket || !isConnected) {
            console.error('Socket não conectado');
            return false;
        }

        socket.emit('entrar-chat', { chatUuid });
        
        // Registrar a sala atual no contexto
        if ((socket as any).setCurrentChatRoom) {
            (socket as any).setCurrentChatRoom(chatUuid);
        }
        
        return true;
    }, [socket, isConnected]);

    /**
     * Emitir evento: sair-chat
     * Para sair da sala de um chat específico
     */
    const sairChat = useCallback((chatUuid: string) => {
        if (!socket || !isConnected) {
            console.error('Socket não conectado');
            return false;
        }

        socket.emit('sair-chat', { chatUuid });
        
        // Limpar a sala atual no contexto
        if ((socket as any).setCurrentChatRoom) {
            (socket as any).setCurrentChatRoom(null);
        }
        
        return true;
    }, [socket, isConnected]);

    // ==================== HOOK HELPER ====================

    /**
     * Hook helper para registrar múltiplos listeners
     * e fazer cleanup automático
     */
    const useWebSocketListener = <T,>(
        event: string,
        callback: (data: T) => void
    ) => {
        useEffect(() => {
            if (!socket) return;

            socket.on(event, callback);

            return () => {
                socket.off(event, callback);
            };
        }, [socket, event, callback]);
    };

    return {
        // Estado da conexão
        isConnected,
        isConnecting,
        reconnectionAttempt,
        error,
        reconnect,
        
        // Listeners
        onNovaMensagemRecebida,
        onNovaMensagemEnviada,
        onNovaMensagem,
        onMensagemEnviada,
        onEntrouChat,
        onSaiuChat,
        onErro,
        onChatLido,
        onAlertaChat,
        onAlertaChatRemovido,
        onStatusPedidoChat,
        onStatusMensagem,
        onMensagemEditada,

        // Emit events
        enviarMensagemWhatsApp,
        entrarChat,
        sairChat,
        
        // Helper
        useWebSocketListener,
    };
};

